import { useCallback, useLayoutEffect } from 'react';
import { Auth } from 'aws-amplify';
import { Hub } from 'aws-amplify/'
import createDatabaseManager from '@dyl/utils/createDatabaseManager';

/**
 * @typedef User
 * @property {string} id
 * @property {string} username
 * @property {{
 *  email: string
 *  sub: string
 *  email_verified: boolean
 * }} attributes
 */

const { Provider: UserManager, use: useRaw } = createDatabaseManager({
    /** @type {() => () => Promise<User>} */
    requestHook: function useUserRequest() {
        return useCallback(async () => {
            try {
                return await Auth.currentUserInfo();
            }
            catch (error) {
                console.error(error);
                return null;
            }
        }, []);
    },
    defaultValue: [],
    contextName: 'UserManager',
    persist: true
});

export default UserManager;

export function useAuth() {
    const {
        data: user,
        status,
        refresh,
        reset,
    } = useRaw();

    useLayoutEffect(() => {
        // Subscribe to auth events, auto-refresh when that happens.
        const unsubscribe = Hub.listen('auth', (...e) => { console.debug('auth call', e); refresh(); });

        // Auto-refresh on startup
        if (status === 'not started' || status === 'error') {
            refresh();
        }

        // Unsubscribe on unmount
        return () => unsubscribe();
    }, []);

    const logOut = useCallback(async () => {
        await Auth.signOut();
        reset();
    }, [reset]);

    return {
        refreshAuth: refresh,
        authStatus: status,
        user,
        isLoggedIn: !!user,
        logOut,
    };
}